/*
  Gallery for the OraLux product section: a Swiper stage driven by a Swiper thumbnail rail.

  Nothing here touches a theme's components, so the section moves between themes unchanged.
  The only outside dependency is Swiper itself, which we self-host.

  Points that are deliberate:

  - Swiper is loaded with `defer`, so it may not have parsed yet. This waits for the global
    and gives up after a bounded number of tries; without Swiper the stage still shows its
    first medium and the rail still scrolls, so polling forever would buy nothing.

  - The rail runs vertically beside the stage on desktop and horizontally under it on mobile.
    That is a direction change, not a style change, so it is rebuilt on breakpoint crossing
    rather than left to CSS.

  - Leaving a slide pauses whatever was playing on it. An embedded player has no API here, so
    its iframe is detached and the src kept on the wrapper - left in place it would carry on
    playing behind the slide that replaced it.
*/
(function () {
  'use strict';

  var SELECTOR = '[data-oralux-gallery]';

  function stopMedia(slide) {
    if (!slide) return;

    slide.querySelectorAll('video').forEach(function (video) {
      if (!video.paused) video.pause();
    });

    slide.querySelectorAll('[data-oralux-embed]').forEach(function (holder) {
      var frame = holder.querySelector('iframe');
      if (frame) {
        frame.remove();
        holder.removeAttribute('data-oralux-loaded');
      }
    });
  }

  function loadEmbed(slide) {
    if (!slide) return;
    var holder = slide.querySelector('[data-oralux-embed]');
    if (!holder || holder.hasAttribute('data-oralux-loaded')) return;

    var frame = document.createElement('iframe');
    frame.src = holder.dataset.oraluxEmbed;
    frame.title = holder.dataset.oraluxEmbedTitle || '';
    frame.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
    frame.setAttribute('allowfullscreen', '');
    holder.appendChild(frame);
    holder.setAttribute('data-oralux-loaded', '');
  }

  function build(root) {
    var stageEl = root.querySelector('[data-oralux-stage]');
    var railEl = root.querySelector('[data-oralux-rail]');
    if (!stageEl) return;

    if (stageEl.swiper) stageEl.swiper.destroy(true, true);
    if (railEl && railEl.swiper) railEl.swiper.destroy(true, true);

    var breakpoint = parseInt(root.getAttribute('data-breakpoint'), 10) || 990;
    var vertical = window.matchMedia('(min-width: ' + breakpoint + 'px)').matches;
    var gap = parseInt(root.getAttribute('data-gap'), 10) || 0;

    var thumbs = null;
    if (railEl && railEl.querySelectorAll('.swiper-slide').length > 1) {
      thumbs = new window.Swiper(railEl, {
        direction: vertical ? 'vertical' : 'horizontal',
        slidesPerView: 'auto',
        spaceBetween: gap,
        freeMode: { enabled: true },
        watchSlidesProgress: true,
        a11y: { enabled: true },
      });
    }

    var stage = new window.Swiper(stageEl, {
      slidesPerView: 1,
      spaceBetween: 0,
      /* One medium at a time; the design never shows a peek here. */
      autoHeight: false,
      a11y: { enabled: true },
      thumbs: thumbs ? { swiper: thumbs } : undefined,
    });

    stage.on('slideChange', function () {
      stage.slides.forEach(function (slide, i) {
        if (i !== stage.activeIndex) stopMedia(slide);
      });
      loadEmbed(stage.slides[stage.activeIndex]);
    });

    loadEmbed(stage.slides[stage.activeIndex]);

    /* Vertical and horizontal are different instances, so the pair is rebuilt when the
       viewport crosses the breakpoint rather than restyled in place. */
    if (!root.dataset.oraluxWatching) {
      root.dataset.oraluxWatching = '1';
      var query = window.matchMedia('(min-width: ' + breakpoint + 'px)');
      var onChange = function () {
        build(root);
      };
      if (query.addEventListener) query.addEventListener('change', onChange);
      else query.addListener(onChange);
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll(SELECTOR).forEach(build);
  }

  function whenSwiperReady(run) {
    if (window.Swiper) {
      run();
      return;
    }
    var tries = 0;
    var timer = setInterval(function () {
      if (window.Swiper) {
        clearInterval(timer);
        run();
      } else if (++tries >= 60) {
        clearInterval(timer);
      }
    }, 50);
  }

  whenSwiperReady(function () {
    initAll();
  });

  document.addEventListener('shopify:section:load', function (event) {
    whenSwiperReady(function () {
      initAll(event.target);
    });
  });
})();
